import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { CURRENCY_LOCALE_MAP } from "@shared/schema";
import type { BaseCountry, BaseCurrency } from "@shared/schema";

export interface OrgSettingsContextType {
  baseCountry: BaseCountry;
  baseCurrency: BaseCurrency;
  currencySymbol: string;
  isLoading: boolean;
  formatCurrency: (amountCents: number | null | undefined) => string;
  formatCurrencyValue: (amount: number | null | undefined) => string;
}

const OrgSettingsContext = createContext<OrgSettingsContextType | null>(null);

export function useOrgSettings() {
  const context = useContext(OrgSettingsContext);
  if (!context) {
    throw new Error("useOrgSettings must be used within an OrgSettingsProvider");
  }
  return context;
}

export function OrgSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();

  // Admin settings — fire only when admin is confirmed logged in
  const { data: adminData, isLoading: adminLoading } = useQuery<{ baseCountry: BaseCountry; baseCurrency: BaseCurrency }>({
    queryKey: ["/api/organization/settings"],
    enabled: !!user,
  });

  // Customer settings — fire only after admin auth check completes and confirms no admin
  const { data: customerData, isLoading: customerLoading } = useQuery<{ baseCountry: BaseCountry; baseCurrency: BaseCurrency }>({
    queryKey: ["/api/customer/organization/settings"],
    enabled: !authLoading && !user,
    retry: false,
  });

  const data = adminData || customerData;
  const isLoading = authLoading || (user ? adminLoading : customerLoading);

  const baseCountry = data?.baseCountry || "AU";
  const baseCurrency = data?.baseCurrency || "AUD";

  const currencySymbol = useMemo(() => {
    const locale = CURRENCY_LOCALE_MAP[baseCurrency] || "en-AU";
    const parts = new Intl.NumberFormat(locale, { style: "currency", currency: baseCurrency }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value || baseCurrency;
  }, [baseCurrency]);

  const formatCurrency = useMemo(() => {
    return (amountCents: number | null | undefined): string => {
      if (amountCents == null) return "-";
      const locale = CURRENCY_LOCALE_MAP[baseCurrency] || "en-AU";
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: baseCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amountCents / 100);
    };
  }, [baseCurrency]);

  const formatCurrencyValue = useMemo(() => {
    return (amount: number | null | undefined): string => {
      if (amount == null) return "-";
      const locale = CURRENCY_LOCALE_MAP[baseCurrency] || "en-AU";
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: baseCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    };
  }, [baseCurrency]);

  return (
    <OrgSettingsContext.Provider value={{ baseCountry, baseCurrency, currencySymbol, isLoading, formatCurrency, formatCurrencyValue }}>
      {children}
    </OrgSettingsContext.Provider>
  );
}
